// DEV PANEL — Bug / suggestion reporter
// Only visible to beta testers / devs.
// Float button bottom-right → slide-in form → submit to Supabase bug_reports.

import { useState, useRef, useEffect } from 'react';
import { submitBugReport } from '../lib/supabase';
import { getSessionData } from '../game/sessionTracker';
import { getPerformanceSummary } from '../game/performanceTracker';
import { isDevUser } from '../game/devUsers';  // BETA 15.56 — centralized DEV_USERS
import CardZoneCalibrator from './CardZoneCalibrator';  // BETA 22.87 r8 — Dylan's card-zone tool

interface DevPanelProps {
  username: string;
  currentScreen: string; // 'menu' | 'game' | 'settings' | etc.
}

const CATEGORIES = ['bug', 'suggestion', 'note'] as const;
type Category = typeof CATEGORIES[number];

const CATEGORY_LABELS: Record<Category, string> = {
  bug:        '🐛  Bug',
  suggestion: '💡  Suggestion',
  note:       '📝  Note',
};

// ── Chalk palette (matches SettingsScreen) ─────────────────────────────────
const C = {
  white:     'rgba(235, 230, 220, 0.92)',
  bright:    'rgba(250, 248, 240, 1.0)',
  dim:       'rgba(210, 205, 195, 0.55)',
  highlight: 'rgba(255, 250, 210, 0.95)',
  board:     'rgba(28, 38, 28, 0.97)',
  border:    'rgba(200, 195, 185, 0.40)',
  danger:    'rgba(255, 100, 80, 0.85)',
  success:   'rgba(100, 220, 140, 0.90)',
};

export default function DevPanel({ username, currentScreen }: DevPanelProps) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [gameVersion, setGameVersion] = useState('unknown');
  const [showCardCal, setShowCardCal] = useState(false);   // 22.87 r8 — card-zone calibrator
  const titleRef  = useRef<HTMLInputElement>(null);
  const panelRef  = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/version.json')
      .then(r => r.json())
      .then(d => setGameVersion(d.version ?? 'unknown'))
      .catch(() => {});
  }, []);

  // Focus title field when panel opens
  useEffect(() => {
    if (open) setTimeout(() => titleRef.current?.focus(), 80);
  }, [open]);

  // Block ALL keyboard events from reaching the game while the panel is open.
  // The game uses native window.addEventListener (bubble phase). Attaching to the
  // panel div only intercepts events that bubble THROUGH it — which fails if focus
  // is elsewhere (e.g. during pause, focus is on the overlay, not the input).
  // Solution: attach to document in bubble phase (fires before window) when open,
  // and remove when closed. This catches every key regardless of which element has focus.
  useEffect(() => {
    if (!open) return;
    const trap = (e: KeyboardEvent) => { e.stopPropagation(); };
    document.addEventListener('keydown',  trap);
    document.addEventListener('keyup',    trap);
    document.addEventListener('keypress', trap);
    return () => {
      document.removeEventListener('keydown',  trap);
      document.removeEventListener('keyup',    trap);
      document.removeEventListener('keypress', trap);
    };
  }, [open]);

  // Only render for dev users
  if (!isDevUser(username)) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      // Attach the latest game-session snapshot to the report
      const { snapshot, events } = getSessionData();
      const sessionPayload = snapshot
        ? { snapshot, recentEvents: events.slice(-30) }
        : null;

      // Auto-append performance data so every report includes FPS/lag info
      const perfSummary = getPerformanceSummary();
      const fullDescription = [
        `[${gameVersion}]`,
        description.trim(),
        perfSummary,
      ].filter(Boolean).join('\n\n');

      await submitBugReport({
        reporter: username,
        category,
        title: title.trim(),
        description: fullDescription,
        screen: currentScreen,
        session_data: sessionPayload as Record<string, unknown> | null,
      });
      setStatus('sent');
      setTitle('');
      setDescription('');
      setCategory('bug');
      setTimeout(() => {
        setStatus('idle');
        setOpen(false);
      }, 1800);
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message ?? 'Submit failed.');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setStatus('idle');
    setErrorMsg('');
  };

  const sharedInputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    color: C.white,
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '0.78rem',
    padding: '7px 9px',
    outline: 'none',
    letterSpacing: '0.05em',
    boxSizing: 'border-box',
  };

  return (
    <>
      {/* ── Floating trigger button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Dev Report"
        style={{
          position: 'fixed',
          bottom: 52,
          right: 16,
          zIndex: 9000,
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: open ? 'rgba(255,100,80,0.18)' : 'rgba(28,38,28,0.92)',
          border: `1px solid ${open ? 'rgba(255,100,80,0.6)' : 'rgba(200,195,185,0.35)'}`,
          cursor: 'pointer',
          fontSize: '1.1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'border-color 0.15s, background 0.15s',
          boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
        }}
      >
        🐛
      </button>

      {/* ── CARD CAL trigger (22.87 r8 — Dylan's zone calibrator) ── */}
      <button
        onClick={() => setShowCardCal(true)}
        title="Card Zone Calibrator"
        style={{
          position: 'fixed',
          bottom: 96,
          right: 16,
          zIndex: 9000,
          width: 38,
          height: 38,
          borderRadius: '50%',
          background: 'rgba(28,38,28,0.92)',
          border: '1px solid rgba(200,195,185,0.35)',
          cursor: 'pointer',
          fontSize: '1.1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
        }}
      >
        🃏
      </button>
      {showCardCal && <CardZoneCalibrator onClose={() => setShowCardCal(false)} />}

      {/* ── Slide-in panel ── */}
      <div
        ref={panelRef}
        style={{
          position: 'fixed',
          bottom: 98,
          right: 16,
          zIndex: 8999,
          width: 300,
          background: C.board,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          boxShadow: '0 4px 24px rgba(0,0,0,0.7)',
          fontFamily: "'Share Tech Mono', monospace",
          overflow: 'hidden',
          // Slide in/out
          maxHeight: open ? 600 : 0,
          opacity: open ? 1 : 0,
          transition: 'max-height 0.22s ease, opacity 0.18s ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px 8px',
          borderBottom: `1px solid ${C.border}`,
        }}>
          <span style={{ color: C.highlight, fontSize: '0.72rem', letterSpacing: '0.2em' }}>
            🐛 DEV REPORT
          </span>
          <span style={{ color: C.dim, fontSize: '0.62rem', letterSpacing: '0.1em' }}>
            {username} · {gameVersion}
          </span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Category */}
          <div>
            <div style={{ color: C.dim, fontSize: '0.65rem', letterSpacing: '0.15em', marginBottom: 5 }}>CATEGORY</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  style={{
                    flex: 1,
                    background: category === cat ? 'rgba(255,250,210,0.12)' : 'transparent',
                    border: `1px solid ${category === cat ? 'rgba(255,250,210,0.5)' : C.border}`,
                    borderRadius: 3,
                    color: category === cat ? C.highlight : C.dim,
                    fontFamily: "'Share Tech Mono', monospace",
                    fontSize: '0.62rem',
                    letterSpacing: '0.08em',
                    padding: '5px 2px',
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                  }}
                >
                  {CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <div style={{ color: C.dim, fontSize: '0.65rem', letterSpacing: '0.15em', marginBottom: 5 }}>TITLE</div>
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Short description..."
              maxLength={120}
              required
              style={sharedInputStyle}
            />
          </div>

          {/* Description */}
          <div>
            <div style={{ color: C.dim, fontSize: '0.65rem', letterSpacing: '0.15em', marginBottom: 5 }}>DETAILS  <span style={{ opacity: 0.5 }}>(optional)</span></div>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Steps to reproduce, extra context..."
              maxLength={1000}
              rows={4}
              style={{ ...sharedInputStyle, resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          {/* Status / submit row */}
          {status === 'error' && (
            <div style={{ color: C.danger, fontSize: '0.68rem', letterSpacing: '0.08em' }}>
              ✕ {errorMsg}
            </div>
          )}
          {status === 'sent' && (
            <div style={{ color: C.success, fontSize: '0.72rem', letterSpacing: '0.12em', textAlign: 'center' }}>
              ✓ REPORT SENT
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                flex: '0 0 auto',
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 3,
                color: C.dim,
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: '0.68rem',
                letterSpacing: '0.1em',
                padding: '7px 12px',
                cursor: 'pointer',
              }}
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={status === 'sending' || status === 'sent' || !title.trim()}
              style={{
                flex: 1,
                background: status === 'sent' ? 'rgba(100,220,140,0.15)' : 'rgba(255,250,210,0.10)',
                border: `1px solid ${status === 'sent' ? 'rgba(100,220,140,0.5)' : 'rgba(255,250,210,0.4)'}`,
                borderRadius: 3,
                color: status === 'sent' ? C.success : C.highlight,
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: '0.72rem',
                letterSpacing: '0.15em',
                padding: '7px 12px',
                cursor: status === 'sending' || !title.trim() ? 'not-allowed' : 'pointer',
                opacity: status === 'sending' ? 0.6 : 1,
                transition: 'all 0.15s',
              }}
            >
              {status === 'sending' ? 'SENDING...' : status === 'sent' ? '✓ SENT' : 'SUBMIT'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
